import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import DashboardLayout from '@/pages/dashboard/components/DashboardLayout';
import { useAuth } from '@/context/AuthContext';
import { supabase } from '@/lib/supabase';

interface ApartmentItem {
  id: string;
  title: string;
  type: string;
  transaction: string;
  city: string;
  quartier: string;
  price: number;
  bedrooms: number;
  bathrooms: number;
  surface: number;
  images: string[];
  available: boolean;
  status: string;
  created_at: string;
}

const statusLabels: Record<string, string> = {
  pending: 'En attente',
  active: 'Publié',
  suspended: 'Suspendu',
};

function formatPrice(price: number) {
  return Number(price || 0).toLocaleString('fr-FR');
}

function ApartmentsDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [apartments, setApartments] = useState<ApartmentItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let mounted = true;

    const load = async () => {
      if (!user) return;
      setLoading(true);
      setError('');
      try {
        const { data: list, error: err } = await supabase
          .from('apartments')
          .select('*')
          .eq('owner_id', user.id)
          .order('created_at', { ascending: false });
        if (err) throw err;
        if (mounted) setApartments((list as ApartmentItem[]) || []);
      } catch (e) {
        if (mounted) setError(e instanceof Error ? e.message : 'Une erreur est survenue.');
      } finally {
        if (mounted) setLoading(false);
      }
    };

    load();
    return () => {
      mounted = false;
    };
  }, [user]);

  const handleDelete = async (id: string) => {
    if (!window.confirm('Supprimer ce bien ? Cette action est irréversible.')) return;
    const { error: delErr } = await supabase.from('apartments').delete().eq('id', id);
    if (delErr) {
      setError(delErr.message);
      return;
    }
    setApartments((prev) => prev.filter((a) => a.id !== id));
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center py-20">
          <div className="w-12 h-12 flex items-center justify-center rounded-full bg-primary-500/10">
            <i className="ri-loader-4-line text-2xl text-primary-400 animate-spin" />
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
        <div>
          <h1 className="font-heading font-bold text-2xl md:text-3xl text-foreground-50">Mes appartements</h1>
          <p className="text-sm text-foreground-400 mt-1">
            Publiez vos biens immobiliers : ils seront visibles sur la page Apartments après validation.
          </p>
        </div>
        <button
          onClick={() => navigate('/dashboard/apartments/new')}
          className="px-5 py-2.5 bg-primary-500 text-foreground-950 font-semibold rounded-lg hover:bg-primary-400 transition-colors text-sm whitespace-nowrap flex items-center gap-2"
        >
          <i className="ri-add-line" />
          Publier un bien
        </button>
      </div>

      {error && (
        <div className="p-4 rounded-lg bg-red-500/10 border border-red-400/30 text-sm text-red-400 mb-6">{error}</div>
      )}

      {apartments.length === 0 ? (
        <div className="text-center py-16 border border-dashed border-background-300/60 rounded-xl">
          <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-background-100 mb-4">
            <i className="ri-home-smile-line text-2xl text-foreground-400" />
          </div>
          <p className="text-foreground-500 font-medium mb-1">Aucun bien publié pour le moment</p>
          <p className="text-sm text-foreground-500 mb-6">
            Commencez par publier votre premier appartement ou terrain.
          </p>
          <button
            onClick={() => navigate('/dashboard/apartments/new')}
            className="inline-block px-6 py-2.5 bg-primary-500 text-foreground-950 font-semibold rounded-lg hover:bg-primary-400 transition-colors text-sm"
          >
            Publier mon premier bien
          </button>
        </div>
      ) : (
        <div className="space-y-4">
          {apartments.map((apt) => (
            <div
              key={apt.id}
              className="flex flex-col sm:flex-row gap-4 p-4 rounded-xl bg-background-100 border border-background-200/70"
            >
              <div className="w-full sm:w-40 h-28 shrink-0 rounded-lg overflow-hidden bg-background-200/60">
                {apt.images && apt.images.length > 0 ? (
                  <img src={apt.images[0]} alt={apt.title} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <i className="ri-image-line text-2xl text-foreground-500" />
                  </div>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between gap-3 mb-1">
                  <h2 className="font-heading font-semibold text-foreground-50 truncate">{apt.title}</h2>
                  <span
                    className={`px-2.5 py-1 rounded-full text-xs font-semibold whitespace-nowrap ${
                      apt.status === 'active'
                        ? 'bg-accent-500/15 text-accent-400'
                        : apt.status === 'suspended'
                          ? 'bg-red-500/15 text-red-400'
                          : 'bg-secondary-500/15 text-secondary-400'
                    }`}
                  >
                    {statusLabels[apt.status] ?? apt.status}
                  </span>
                </div>
                <p className="text-sm text-foreground-400 mb-2">
                  {apt.type} · {apt.quartier}, {apt.city} · {apt.bedrooms} ch. · {apt.surface} m²
                </p>
                <div className="flex flex-wrap items-center gap-4 text-sm">
                  <span className="font-heading font-bold text-primary-400">
                    {formatPrice(apt.price)} FCFA{apt.transaction === 'location' ? ' / mois' : ''}
                  </span>
                  <span className="text-foreground-400">
                    {apt.transaction === 'location' ? 'À louer' : 'À vendre'}
                  </span>
                  {!apt.available && (
                    <span className="text-xs text-foreground-500 px-2 py-0.5 rounded-full bg-background-200/60">
                      Indisponible
                    </span>
                  )}
                </div>
              </div>
              <div className="flex sm:flex-col gap-2 shrink-0 sm:justify-center">
                <Link
                  to={`/dashboard/apartments/${apt.id}/edit`}
                  className="flex-1 sm:flex-none px-3 py-2 text-center text-sm font-medium border border-background-300/60 text-foreground-50 rounded-md hover:bg-background-200/60 transition-colors whitespace-nowrap"
                >
                  Modifier
                </Link>
                <button
                  onClick={() => handleDelete(apt.id)}
                  className="flex-1 sm:flex-none px-3 py-2 text-sm font-medium text-red-400 border border-red-400/30 rounded-md hover:bg-red-500/10 transition-colors whitespace-nowrap"
                >
                  Supprimer
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </DashboardLayout>
  );
}

export default ApartmentsDashboard;